import React, { Fragment, ReactNode } from 'react';
import { Breakpoint } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent, KEYS, ResponsiveComponentBase } from '../../../shared/index.js';
import Button from '../Button/Button.js';
import { IButtonProps } from '../Button/models/index.js';
import Image from '../Image/Image.js';
import { ITranslationContextValue } from '../Translate/models/ITranslationContextValue.js';
import { TranslationContext } from '../Translate/Translate.js';

import DotWrapperAccessible from './components/DotWrapper/index.js';
import { ICarouselConfiguration, ICarouselProps, ICarouselState } from './models/index.js';
import Components from './styledComponents/Components.js';
import ComponentWrapper from './styledComponents/ComponentWrapper.js';
import Content from './styledComponents/Content.js';
import ControlsWrapper from './styledComponents/ControlsWrapper.js';
import Dot from './styledComponents/Dot.js';
import Dots from './styledComponents/Dots.js';
import Nav from './styledComponents/Nav.js';
import NavLeft from './styledComponents/NavLeft.js';
import NavRight from './styledComponents/NavRight.js';
import SelectedDot from './styledComponents/SelectedDot.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CAROUSEL_CATEGORY_KEY, CAROUSEL_COMPONENT_KEY } from './Carousel.meta.js';

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

const NAVIGATION_KEYS = [KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW, KEYS.UP_ARROW, KEYS.DOWN_ARROW, KEYS.HOME, KEYS.END];

class CarouselComponent extends ComponentBase<ICarouselProps, ICarouselState, ICarouselConfiguration> {
	categoryKey = CAROUSEL_CATEGORY_KEY;
	componentKey = CAROUSEL_COMPONENT_KEY;

	autoPlayId: any;
	isAutoPlayEnabled = true;
	isStopped = false;
	hasFocus = false;
	hasHover = false;
	controlReferences: HTMLElement[] = [];
	dotsID: string[] = [];
	translationContext: ITranslationContextValue | undefined;

	static defaultProps: ICarouselProps = {
		currentIndex: 0,
	};
	constructor(props: ICarouselProps) {
		super(props);

		makeObservable(this, {
			isStopped: observable,
			hasFocus: observable,
			hasHover: observable,
			componentsToShow: computed,
			pageCount: computed,
			onWrapperFocus: action,
			onWrapperBlur: action,
			toggleHover: action,
			stopStartButtonProps: computed,
			handleStopButtonOnClick: action,
			handleStartButtonOnClick: action,
		});
		this.state = {
			currentIndex: props.currentIndex || 0,
		};
		this.setAutoPlay();
		this.dotsID = this.getDotsID();
	}

	getDotsID() {
		const dotIds: string[] = [];
		for (let page = 0; page < this.pageCount; page++) {
			dotIds.push(`dotsID-${page}-${common.commonUtils.generateGuid()}`);
		}
		return dotIds;
	}

	assignReferenceToControlID = (controlElement: any | null) => {
		this.controlReferences.push(controlElement);
	};

	componentWillUnmount() {
		clearInterval(this.autoPlayId);
	}

	findElementByID(elementID: string): HTMLElement | undefined {
		return this.controlReferences.find((elem) => {
			if (elem && elem.id === elementID) {
				// offsetParent will if the element or its parent element has the display property set to none.
				// https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
				return elem.offsetParent !== null;
			}
			return false;
		});
	}

	get componentsToShow(): number {
		const { componentsToShow = 1 } = this.props;
		return componentsToShow > 0 ? componentsToShow : 1;
	}
	get pageCount(): number {
		return Math.ceil(this.componentCount / this.componentsToShow);
	}
	get currentIndex(): number {
		return this.state.currentIndex || 0;
	}
	get childComponentArray(): ReactNode[] {
		const { children } = this.props;
		return React.Children.toArray(children) || [];
	}
	get currentComponents(): ReactNode {
		const childComponentArray = this.childComponentArray;
		if (common.commonUtils.isNullOrEmpty(childComponentArray)) {
			return null;
		}

		const pagedComponents: any[] = [];
		for (let index = 0; index < this.componentCount; index += this.componentsToShow) {
			pagedComponents.push(childComponentArray.slice(index, index + this.componentsToShow));
		}

		return pagedComponents.map((page, pageIndex) => {
			const isDisplaying = this.currentIndex === pageIndex;
			const componentStyle: IComponentStyle = {
				display: isDisplaying ? 'block' : 'none',
				flex: {
					basis: `100%`,
					shrink: '0',
				},
			};
			return (
				<ComponentWrapper componentStyle={componentStyle} key={pageIndex} role="tabpanel">
					{page}
				</ComponentWrapper>
			);
		});
	}
	get componentCount(): number {
		return this.childComponentArray.length;
	}
	handleDotClick = (index: number) => {
		this.disabledAutoPlay();
		this.changeIndex(index);
	};
	handleDotKeyDown = (e: React.KeyboardEvent<HTMLElement>, index: number) => {
		const { key } = e;
		if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1) {
			this.handleDotClick(index);
		}

		if (NAVIGATION_KEYS.indexOf(key as KEYS) !== -1) {
			const lastIndex = this.dotsID.length - 1;
			let nextIndex = index;
			switch (key) {
				case KEYS.LEFT_ARROW:
				case KEYS.UP_ARROW:
					nextIndex -= 1;
					break;
				case KEYS.RIGHT_ARROW:
				case KEYS.DOWN_ARROW:
					nextIndex += 1;
					break;
				case KEYS.HOME:
					nextIndex = 0;
					break;
				case KEYS.END:
					nextIndex = lastIndex;
					break;
				default:
					break;
			}
			if (nextIndex < 0) {
				nextIndex = lastIndex;
			}
			if (nextIndex > lastIndex) {
				nextIndex = 0;
			}
			const newElement = this.findElementByID(this.dotsID[nextIndex]);
			if (newElement) {
				newElement.focus();
			}
		}
	};
	changeIndex = (index: number) => {
		const { currentIndex } = this.state;
		const componentCount = this.componentCount;
		const newIndex = Math.min(componentCount - 1, Math.max(0, index));
		if (currentIndex !== newIndex) {
			this.setAutoPlay();
			this.setState({ currentIndex: index });
		}
	};
	moveFromIndex = (offset: number) => {
		const { infiniteScroll = false } = this.props;
		const { currentIndex } = this.state;
		let adjustedIndex = (currentIndex || 0) + offset;
		if (adjustedIndex < 0) {
			if (!infiniteScroll) {
				return;
			}
			adjustedIndex = this.pageCount - 1;
		}
		if (adjustedIndex > this.pageCount - 1) {
			if (!infiniteScroll) {
				return;
			}
			adjustedIndex = 0;
		}
		this.changeIndex(adjustedIndex);
	};
	setAutoPlay = () => {
		const { autoPlayInterval } = this.props;
		if (this.autoPlayId) {
			clearInterval(this.autoPlayId);
		}
		const componentCount = this.componentCount;
		if (autoPlayInterval && componentCount > 1 && this.isAutoPlayEnabled) {
			this.autoPlayId = setInterval(() => this.moveFromIndex(1), autoPlayInterval);
		}
	};

	handleNavClick = (offset: number) => {
		this.disabledAutoPlay();
		this.moveFromIndex(offset);
	};
	handleNavKeyUp = (e: React.KeyboardEvent<HTMLElement>, direction: number) => {
		const { key } = e;
		if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1) {
			this.handleNavClick(direction);
		}
	};

	enabledAutoPlay = () => {
		if (!this.isAutoPlayEnabled && !this.isStopped) {
			if (!this.hasFocus && !this.hasHover) {
				this.isAutoPlayEnabled = true;
				this.setAutoPlay();
			}
		}
	};

	disabledAutoPlay = () => {
		this.isAutoPlayEnabled = false;
		this.setAutoPlay();
	};

	get showNavigation(): boolean {
		const { navigationHidesWhenSingle = false } = this.props;
		return this.pageCount > 1 || !navigationHidesWhenSingle;
	}

	get showLeftNavigation(): boolean {
		const { infiniteScroll = true } = this.props;
		if (this.showNavigation) {
			return infiniteScroll || this.currentIndex !== 0;
		}
		return false;
	}

	get showRightNavigation(): boolean {
		const { infiniteScroll = true } = this.props;
		if (this.showNavigation) {
			return infiniteScroll || this.currentIndex + 1 < this.pageCount;
		}
		return false;
	}

	isDotSelected(index: number) {
		return index === this.currentIndex;
	}

	onWrapperFocus = () => {
		this.hasFocus = true;
		this.disabledAutoPlay();
	};

	onWrapperBlur = () => {
		this.hasFocus = false;
		this.enabledAutoPlay();
	};

	toggleHover = () => {
		this.hasHover = !this.hasHover;
		this.hasHover ? this.disabledAutoPlay() : this.enabledAutoPlay();
	};

	get stopStartButtonProps(): IButtonProps {
		let stopText = 'Stop Carousel';
		let startText = 'Start Carousel';
		if (this.translationContext) {
			const translate = this.translationContext.translate;
			stopText = translate(stopText);
			startText = translate(startText);
		}
		const buttonProps: IButtonProps = {};
		if (!this.isStopped) {
			return Object.assign(buttonProps, {
				text: stopText,
				onClick: this.handleStopButtonOnClick,
			});
		}
		return Object.assign(buttonProps, {
			text: startText,
			onClick: this.handleStartButtonOnClick,
		});
	}

	handleStopButtonOnClick = () => {
		this.isStopped = true;
		this.disabledAutoPlay();
	};

	handleStartButtonOnClick = () => {
		this.isStopped = false;
		// This is the only time the silde show can start without the checks
		this.isAutoPlayEnabled = true;
		this.setAutoPlay();
	};

	skipAcceptKeys = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (ACCEPT_KEYS.indexOf(e.key as KEYS) !== -1) {
			e.preventDefault();
		}
	};

	render() {
		const { className, dotChildren, navLeftChildren, navRightChildren, hideDots = false } = this.props;
		const dotList = Array(this.pageCount).fill('');
		const wrapperEvents = {
			onMouseEnter: this.toggleHover,
			onMouseLeave: this.toggleHover,
			onFocus: this.onWrapperFocus,
			onBlur: this.onWrapperBlur,
		};
		let dotLabel = 'Select slide';
		let nextSlideLabel = 'Next slide';
		let prevSlideLabel = 'Previous slide';
		if (this.translationContext) {
			const translate = this.translationContext.translate;
			dotLabel = translate(dotLabel);
			nextSlideLabel = translate(nextSlideLabel);
			prevSlideLabel = translate(prevSlideLabel);
		}
		const slideWrapperProps: Record<string, unknown> = {
			'aria-atomic': false,
			'aria-live': this.isAutoPlayEnabled ? 'off' : 'polite',
		};

		return (
			<Fragment>
				<TranslationContext.Consumer>
					{(translationContext) => {
						this.translationContext = translationContext;
						return null;
					}}
				</TranslationContext.Consumer>
				<Wrapper className={className} {...wrapperEvents}>
					<ControlsWrapper>
						<Button {...this.stopStartButtonProps} />
						{this.showLeftNavigation && (
							<NavLeft
								aria-label={prevSlideLabel}
								onClick={() => {
									this.handleNavClick(-1);
								}}
								onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => this.skipAcceptKeys(e)}
								onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => this.handleNavKeyUp(e, -1)}
							>
								<Nav navIcon={'base.carousel.navLeft'}>{navLeftChildren || <span>&lt;</span>}</Nav>
							</NavLeft>
						)}
						{this.showRightNavigation && (
							<NavRight
								aria-label={nextSlideLabel}
								onClick={() => this.handleNavClick(1)}
								onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => this.skipAcceptKeys(e)}
								onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => this.handleNavKeyUp(e, 1)}
							>
								<Nav navIcon={'base.carousel.navRight'}>{navRightChildren || <span>&gt;</span>}</Nav>
							</NavRight>
						)}
						{!hideDots && this.showNavigation && (
							<Dots role="tablist">
								{dotList.map((e, index) => {
									const selected = this.isDotSelected(index);
									const DotComponent = selected ? SelectedDot : Dot;
									const asset = selected ? 'base.carousel.dotSelected' : 'base.carousel.dot';
									return (
										<DotWrapperAccessible
											id={this.dotsID[index]}
											key={`dot_${index}`}
											role="tab"
											aria-label={dotLabel}
											aria-setsize={dotList.length}
											aria-posinset={index + 1}
											aria-selected={this.isDotSelected(index)}
											tabIndex={this.isDotSelected(index) ? 0 : -1}
											onClick={() => this.handleDotClick(index)}
											onKeyDown={(e: any) => this.handleDotKeyDown(e, index)}
											assignDotsRef={(instance: any) => {
												this.assignReferenceToControlID(instance);
											}}
										>
											<DotComponent
												selected={this.isDotSelected(index)}
												onClick={() => this.handleDotClick(index)}
											>
												<Image asset={asset} className={className} alt={dotChildren} />
											</DotComponent>
										</DotWrapperAccessible>
									);
								})}
							</Dots>
						)}
					</ControlsWrapper>
					<Content>
						<Components
							componentStyle={{ display: 'flex', width: '100%', overflow: { _value: 'hidden' } }}
							{...slideWrapperProps}
						>
							{this.currentComponents}
						</Components>
					</Content>
				</Wrapper>
			</Fragment>
		);
	}
}

const ObserverCarouselComponent = observer(CarouselComponent);

export class Carousel extends ResponsiveComponentBase<ICarouselProps, ICarouselState, ICarouselConfiguration> {
	categoryKey = CAROUSEL_CATEGORY_KEY;
	componentKey = CAROUSEL_COMPONENT_KEY;

	render() {
		const breakpoints = this.breakpoints;
		if (breakpoints) {
			return (
				<Fragment>
					{breakpoints.map((breakpoint, breakpointIndex) => {
						const props = common.commonUtils.deepMergeObjectsWithOptions(
							{ arrayMergeStrategy: 'DeepMerge' },
							this.props,
							breakpoint.props,
						);
						const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
						return (
							<Breakpoint {...breakpointProps}>
								<ObserverCarouselComponent {...props} />
							</Breakpoint>
						);
					})}
				</Fragment>
			);
		}
		return <ObserverCarouselComponent {...this.props} />;
	}
}

export default InjectedStyledComponent(Carousel, CAROUSEL_CATEGORY_KEY, CAROUSEL_COMPONENT_KEY);
